'use strict';

/**
 * @ngdoc type
 * @name mx.shell.Object
 * @module mx.shell
 *
 * @description
 * A generic object implementation containing basic information of the business entities
 *
 * @property {string} _type - Gets or sets type of the object (e.g. `incident`, `task`, `user`).
 * @property {string} _id - Gets or sets identity of the object.
 * @property {string} _name - Gets or sets short name of the object.
 * @property {string} _displayName - Gets or sets display name of the object.
 * @property {int} _state - Gets or sets state of the object (e.g. SPSCommonClassBase.State).
 */
function MxObject(type, id, name, state, displayName) {
	Object.defineProperty(this, '_type', {
		writable: true,
		value: type,
	});
	Object.defineProperty(this, '_id', {
		writable: true,
		value: id,
	});
	Object.defineProperty(this, '_name', {
		writable: true,
		value: name,
	});
	Object.defineProperty(this, '_displayName', {
		writable: true,
		value: displayName,
	});
	Object.defineProperty(this, '_state', {
		writable: true,
		value: state,
	});
}

/**
 * @ngdoc type
 * @name mx.shell.ObjectProperty
 * @module mx.shell
 *
 * @description
 * An implementation of the definition of a business entity's property
 *
 * @property {string} name - Gets or sets internal name of the object property.
 * @property {string} title - Gets or sets display name of the object property.
 * @property {mx.shell.ObjectPropertyType} type - Gets or sets type of the object property. See {@ref mx.shell.ObjectPropertyType mx.shell.ObjectPropertyType} for available values
 * @property {mx.shell.ObjectPropertyEnumValue[]} values - Gets or sets a collection of available values for the object property. An array of {@ref mx.shell.ObjectPropertyEnumValue mx.shell.ObjectPropertyEnumValue}
 */
function MxObjectProperty(name, title, type) {
	Object.defineProperty(this, 'name', {
		writable: true,
		value: name,
	});
	Object.defineProperty(this, 'title', {
		writable: true,
		value: title,
	});
	Object.defineProperty(this, 'type', {
		writable: true,
		value: type || MxObjectPropertyType.String.name,
	});
	Object.defineProperty(this, 'values', { writable: true });
}

/**
 * @ngdoc enum
 * @name mx.shell.ObjectPropertyType
 * @module mx.shell
 *
 * @description
 * Enum for object property data types
 */
var MxObjectPropertyType = {
	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#String
	 *
	 * @description
	 * string data type
	 */
	String: {
		name: 'StringType',
		title: 'String',
	},

	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#Guid
	 *
	 * @description
	 * Guid data type
	 */
	Guid: {
		name: 'GuidType',
		title: 'Guid',
	},

	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#Boolean
	 *
	 * @description
	 * Boolean data type
	 */
	Boolean: {
		name: 'BoolType',
		title: 'Boolean',
	},

	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#Integer
	 *
	 * @description
	 * integer data type
	 */
	Integer: {
		name: 'IntType',
		title: 'Int',
	},

	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#Float
	 *
	 * @description
	 * float data type
	 */
	Float: {
		name: 'FloatType',
		title: 'Float',
	},

	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#Long
	 *
	 * @description
	 * long data type
	 */
	Long: {
		name: 'LongType',
		title: 'Long',
	},

	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#Decimal
	 *
	 * @description
	 * decimal data type
	 */
	Decimal: {
		name: 'DecimalType',
		title: 'Decimal',
	},

	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#Date
	 *
	 * @description
	 * date and time data type
	 */
	Date: {
		name: 'DateType',
		title: 'Date',
	},

	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#Text
	 *
	 * @description
	 * Guid data type
	 */
	Text: {
		name: 'TextType',
		title: 'Text',
	},

	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#Currency
	 *
	 * @description
	 * Currency type
	 */
	Currency: {
		name: 'Currency',
		title: 'Currency',
	},

	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#Icon
	 *
	 * @description
	 * Icon type
	 */
	Icon: {
		name: 'icon',
		title: 'Icon',
	},

	/**
	 * @ngdoc property
	 * @name mx.shell.ObjectPropertyType#Color
	 *
	 * @description
	 * Color type
	 */
	Color: {
		name: 'color',
		title: 'Color',
	},
};

/**
 * @ngdoc type
 * @name mx.shell.ObjectPropertyEnumValue
 * @module mx.shell
 *
 * @description
 * An implementation of an object property values
 *
 * @property {object} value - Gets or sets an object property value
 * @property {string} title - Gets or sets a title for an object property value. If null {@ref mx.shell.ObjectPropertyEnumValue#value value} is used
 */
function MxObjectPropertyEnumValue(value, title) {
	Object.defineProperty(this, 'value', {
		writable: true,
		value: value,
	});
	Object.defineProperty(this, 'title', {
		writable: true,
		value: title || value,
	});
}

const w = window;
w.mx = w.mx || {};
w.mx.shell = w.mx.shell || {};
w.mx.shell.Object = MxObject;
w.mx.shell.ObjectProperty = MxObjectProperty;
w.mx.shell.ObjectPropertyType = MxObjectPropertyType;
w.mx.shell.ObjectPropertyEnumValue = MxObjectPropertyEnumValue;

export { MxObject, MxObjectProperty, MxObjectPropertyType, MxObjectPropertyEnumValue };
